import { AttendeeFormDialog } from '@/components/AttendeeFormDialog';
import { AttendeeModalityBadge } from '@/components/AttendeeModalityBadge';
import { AttendeeSourceBadge } from '@/components/AttendeeSourceBadge';
import { BroadcastDialog } from '@/components/BroadcastDialog';
import { CopyToEventDialog } from '@/components/CopyToEventDialog';
import { SendAllDialog } from '@/components/SendAllDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebounce } from '@/hooks/use-debounce';
import { useEvents } from '@/hooks/use-events';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  type Attendee,
  Attendee as AttendeeSchema,
  type AttendeesListResponse,
  AttendeesListResponse as AttendeesListResponseSchema,
  type Modality,
  type ToggleAttendeeStatusInput,
} from '@tunodo/ticketing-shared';
import {
  Award,
  Copy,
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Send,
  SlidersHorizontal,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

// ─── Search params schema ────────────────────────────────────────────────────

const SearchParamsSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().default(10),
  q: z.string().optional(),
  eventId: z.string().uuid().optional(),
  modality: z.enum(['VIRTUAL', 'PRESENCIAL']).optional(),
});
type SearchParams = z.infer<typeof SearchParamsSchema>;

// ─── Column definitions ───────────────────────────────────────────────────────

const ALL_EVENT_SENTINEL = '__all__';
const PAGE_SIZE_OPTIONS = [10, 15, 20, 30] as const;
const VISIBLE_COLUMNS_KEY = 'attendees:visible-columns';

interface ColumnDef {
  key: string;
  label: string;
  alwaysVisible?: boolean;
  render: (attendee: Attendee, ctx: ColumnRenderContext) => React.ReactNode;
}

interface ColumnRenderContext {
  eventsMap: Record<string, string>;
  onSetStatus: (attendee: Attendee, type: 'ATTENDANCE' | 'LUNCH', enabled: boolean) => void;
  statusPending: boolean;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'fullName',
    label: 'Nombre',
    alwaysVisible: true,
    render: (a) => <span className="font-medium">{a.fullName}</span>,
  },
  {
    key: 'email',
    label: 'Email',
    render: (a) => <span className="text-muted-foreground">{a.email}</span>,
  },
  {
    key: 'phone',
    label: 'Teléfono',
    render: (a) => <span className="text-muted-foreground">{a.phone}</span>,
  },
  {
    key: 'profesion',
    label: 'Profesión',
    render: (a) => (
      <span className="text-muted-foreground">
        {a.profesion ?? <span className="opacity-40">—</span>}
      </span>
    ),
  },
  {
    key: 'empresa',
    label: 'Empresa',
    render: (a) => (
      <span className="text-muted-foreground">
        {a.empresa ?? <span className="opacity-40">—</span>}
      </span>
    ),
  },
  {
    key: 'modality',
    label: 'Modalidad',
    render: (a) => (
      <span className="inline-flex items-center gap-1">
        {a.modality ? (
          <AttendeeModalityBadge modality={a.modality} />
        ) : (
          <span className="opacity-40">—</span>
        )}
        <AttendeeSourceBadge source={a.source} />
      </span>
    ),
  },
  {
    key: 'evento',
    label: 'Evento',
    render: (a, ctx) => (
      <span className="text-muted-foreground text-xs">
        {ctx.eventsMap[a.eventId] ?? <span className="opacity-40">—</span>}
      </span>
    ),
  },
  {
    key: 'asistencia',
    label: 'Asistencia',
    render: (a, ctx) => (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {a.hasAttended ? (
          <Badge className="bg-green-100 text-green-700 border-green-200">Asistió</Badge>
        ) : (
          <Badge variant="secondary">Pendiente</Badge>
        )}
        <Switch
          checked={a.hasAttended}
          disabled={ctx.statusPending}
          onCheckedChange={(checked) => ctx.onSetStatus(a, 'ATTENDANCE', checked)}
          aria-label="Toggle asistencia"
        />
      </div>
    ),
  },
  {
    key: 'almuerzo',
    label: 'Almuerzo',
    render: (a, ctx) => (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {a.hasClaimedLunch ? (
          <Badge className="bg-green-100 text-green-700 border-green-200">Reclamado</Badge>
        ) : (
          <Badge variant="secondary">Pendiente</Badge>
        )}
        <Switch
          checked={a.hasClaimedLunch}
          disabled={ctx.statusPending}
          onCheckedChange={(checked) => ctx.onSetStatus(a, 'LUNCH', checked)}
          aria-label="Toggle almuerzo"
        />
      </div>
    ),
  },
  // "acciones" column is always visible, handled separately (not in toggle menu)
];

const DEFAULT_VISIBLE_COLUMNS = new Set([
  'fullName',
  'email',
  'phone',
  'modality',
  'evento',
  'asistencia',
  'almuerzo',
]);

const ALL_MODALITY_SENTINEL = '__all__';

function loadVisibleColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(VISIBLE_COLUMNS_KEY);
    if (!raw) return new Set(DEFAULT_VISIBLE_COLUMNS);
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(DEFAULT_VISIBLE_COLUMNS);
    const validKeys = new Set(COLUMNS.map((c) => c.key));
    const filtered = (parsed as unknown[]).filter(
      (v): v is string => typeof v === 'string' && validKeys.has(v),
    );
    // Always include alwaysVisible columns
    for (const c of COLUMNS.filter((col) => col.alwaysVisible)) {
      filtered.push(c.key);
    }
    return new Set(filtered);
  } catch {
    return new Set(DEFAULT_VISIBLE_COLUMNS);
  }
}

// ─── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_app/attendees/')({
  validateSearch: (search: Record<string, unknown>): SearchParams =>
    SearchParamsSchema.parse(search),
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden ver la lista de inscritos.');
      throw redirect({ to: '/' });
    }
  },
  component: AttendeesPage,
});

// ─── Component ───────────────────────────────────────────────────────────────

function AttendeesPage() {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { page, pageSize, q, eventId, modality } = search;

  // ── Local search input (not URL-driven directly) ──
  const [searchInput, setSearchInput] = useState(() => q ?? '');
  const debouncedQ = useDebounce(searchInput, 300);

  // ── Sync debounced q to URL search params ──
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only run on debounce settle
  useEffect(() => {
    const currentQ = q ?? '';
    const newQ = debouncedQ.trim() || undefined;
    if ((newQ ?? '') !== currentQ) {
      void navigate({
        search: (s) => ({ ...s, q: newQ, page: 1 }),
        replace: true,
      });
    }
  }, [debouncedQ]);

  // ── Column visibility ──
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => loadVisibleColumns());

  useEffect(() => {
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...visibleColumns]));
  }, [visibleColumns]);

  // ── Dialog state ──
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [sendCertOpen, setSendCertOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editAttendee, setEditAttendee] = useState<Attendee | null>(null);
  const [copyAttendee, setCopyAttendee] = useState<Attendee | null>(null);
  const [deleteAttendee, setDeleteAttendee] = useState<Attendee | null>(null);

  // ── Data ──
  const { eventsMap, events } = useEvents();

  const queryKey = ['attendees', { page, pageSize, q: q?.trim(), eventId }];
  const { data, isLoading, isError } = useQuery<AttendeesListResponse>({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (q?.trim()) params.set('q', q.trim());
      if (eventId) params.set('eventId', eventId);
      // La API de Go ignora `modality` (solo acepta q, page, pageSize y eventId), asi que
      // no se envia y el filtro se aplica del lado del cliente mas abajo.
      return apiFetch(`/api/attendees?${params.toString()}`, {
        schema: AttendeesListResponseSchema,
      });
    },
  });

  // Filtro client-side por modalidad. Limitacion: filtra solo la pagina cargada, asi que una
  // pagina puede quedar con menos filas y `total` sigue contando todos los inscritos.
  const filteredData = useMemo<AttendeesListResponse | undefined>(() => {
    if (!data) return data;
    if (!modality) return data;
    const items = data.items.filter((a) => a.modality === modality);
    return { ...data, items };
  }, [data, modality]);

  // ── Mutations ──
  const setStatusMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ToggleAttendeeStatusInput }) =>
      apiFetch(`/api/attendees/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        schema: AttendeeSchema,
      }),
    onSuccess: (updated, { body }) => {
      const label = body.type === 'ATTENDANCE' ? 'Asistencia' : 'Almuerzo';
      const action = body.enabled ? 'marcada' : 'revocada';
      toast.success(`${label} ${action}`);
      void queryClient.invalidateQueries({ queryKey: ['attendees'] });
      void queryClient.invalidateQueries({ queryKey: ['attendee', updated.id] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/attendees/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Inscrito eliminado');
      void queryClient.invalidateQueries({ queryKey: ['attendees'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Derived ──
  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;
  const from = data && data.total > 0 ? (page - 1) * pageSize + 1 : 0;
  const to = data ? Math.min(page * pageSize, data.total) : 0;

  const visibleColDefs = useMemo(
    () => COLUMNS.filter((c) => c.alwaysVisible || visibleColumns.has(c.key)),
    [visibleColumns],
  );

  const colSpan = visibleColDefs.length + 1; // +1 for actions column

  const renderCtx: ColumnRenderContext = {
    eventsMap,
    onSetStatus: (attendee, type, enabled) => {
      setStatusMutation.mutate({ id: attendee.id, body: { type, enabled } });
    },
    statusPending: setStatusMutation.isPending,
  };

  // ── Helpers ──
  function setSearchParam(updates: Partial<SearchParams>) {
    void navigate({ search: (s) => ({ ...s, ...updates }) });
  }

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // ── Skeleton columns for loading ──
  const skeletonCols = visibleColDefs.length + 1;

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inscritos</h1>
          <div className="text-muted-foreground mt-1 text-sm">
            {isLoading ? <Skeleton className="h-4 w-28" /> : <>{data?.total ?? 0} inscritos</>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => setSendAllOpen(true)}>
            <Send className="h-4 w-4" />
            Enviar boletas
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setSendCertOpen(true)}
            disabled={!eventId}
            title={!eventId ? 'Selecciona un evento para enviar certificados' : undefined}
          >
            <Award className="h-4 w-4" />
            Enviar certificados
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/attendees/import">
              <Upload className="h-4 w-4" />
              Importar CSV
            </Link>
          </Button>
          <Button className="gap-2" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Agregar inscrito
          </Button>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Event filter */}
        <Select
          value={eventId ?? ALL_EVENT_SENTINEL}
          onValueChange={(val) =>
            setSearchParam({
              eventId: val === ALL_EVENT_SENTINEL ? undefined : val,
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Todos los eventos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EVENT_SENTINEL}>Todos los eventos</SelectItem>
            {events.map((event) => (
              <SelectItem key={event.id} value={event.id}>
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Modality filter */}
        <Select
          value={modality ?? ALL_MODALITY_SENTINEL}
          onValueChange={(val) =>
            setSearchParam({
              modality: val === ALL_MODALITY_SENTINEL ? undefined : (val as Modality),
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Todas las modalidades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_MODALITY_SENTINEL}>Todas</SelectItem>
            <SelectItem value="VIRTUAL">Virtual</SelectItem>
            <SelectItem value="PRESENCIAL">Presencial</SelectItem>
          </SelectContent>
        </Select>

        {/* Column visibility */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Columnas
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Columnas visibles</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COLUMNS.filter((c) => !c.alwaysVisible).map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visibleColumns.has(col.key)}
                onCheckedChange={() => toggleColumn(col.key)}
              >
                {col.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="rounded-md border overflow-x-auto">
          <Table style={{ minWidth: `${skeletonCols * 140}px` }}>
            <TableHeader>
              <TableRow>
                {visibleColDefs.map((col) => (
                  <TableHead key={col.key}>{col.label}</TableHead>
                ))}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 6 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
                <TableRow key={i}>
                  {visibleColDefs.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full max-w-[120px]" />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Skeleton className="h-4 w-6" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-6 text-center space-y-3">
          <p className="text-sm text-destructive font-medium">
            No se pudo cargar la lista de inscritos.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['attendees'] })}
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* Data table */}
      {!isLoading && !isError && (
        <div className="rounded-md border overflow-x-auto">
          <Table style={{ minWidth: `${Math.max(600, (visibleColDefs.length + 1) * 130)}px` }}>
            <TableHeader>
              <TableRow>
                {visibleColDefs.map((col) => (
                  <TableHead key={col.key}>{col.label}</TableHead>
                ))}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!filteredData?.items.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center py-10 text-muted-foreground">
                    {q?.trim() ? (
                      <>No hay inscritos que coincidan con &ldquo;{q}&rdquo;.</>
                    ) : (
                      <>
                        Aún no hay inscritos.{' '}
                        <Badge variant="outline" className="ml-1">
                          Importa el CSV para empezar.
                        </Badge>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.items.map((attendee) => (
                  <TableRow
                    key={attendee.id}
                    className="cursor-pointer hover:bg-accent/50"
                    onClick={() =>
                      void navigate({ to: '/attendees/$id', params: { id: attendee.id } })
                    }
                  >
                    {visibleColDefs.map((col) => (
                      <TableCell key={col.key}>{col.render(attendee, renderCtx)}</TableCell>
                    ))}
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Acciones</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              void navigate({
                                to: '/attendees/$id',
                                params: { id: attendee.id },
                              })
                            }
                          >
                            <Eye className="h-4 w-4" />
                            Ver detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditAttendee(attendee)}>
                            <Pencil className="h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCopyAttendee(attendee)}>
                            <Copy className="h-4 w-4" />
                            Copiar a otro evento
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteAttendee(attendee)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && data && data.total > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {from}–{to} de {data.total} inscritos
          </p>
          <div className="flex items-center gap-3">
            {/* Page size selector */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Filas:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(val) => setSearchParam({ pageSize: Number(val), page: 1 })}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prev/Next */}
            <Pagination className="w-auto mx-0">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={page > 1 ? () => setSearchParam({ page: page - 1 }) : undefined}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 py-1.5 text-sm">
                    {page} / {totalPages}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={
                      page < totalPages ? () => setSearchParam({ page: page + 1 }) : undefined
                    }
                    aria-disabled={page >= totalPages}
                    className={
                      page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <SendAllDialog
        open={sendAllOpen}
        onOpenChange={setSendAllOpen}
        totalAttendees={data?.total ?? 0}
        eventId={eventId}
      />

      {eventId && (
        <BroadcastDialog
          open={sendCertOpen}
          onOpenChange={setSendCertOpen}
          messageType="CERTIFICATE"
          title="Enviar certificados por email"
          description="Envía el certificado de participación a los inscritos por correo electrónico."
          totalAttendees={data?.total ?? 0}
          eventId={eventId}
          showModalityFilter
        />
      )}

      <AttendeeFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        defaultEventId={eventId}
      />

      {editAttendee && (
        <AttendeeFormDialog
          open={!!editAttendee}
          onOpenChange={(v) => {
            if (!v) setEditAttendee(null);
          }}
          mode="edit"
          attendee={editAttendee}
        />
      )}

      {copyAttendee && (
        <CopyToEventDialog
          open={!!copyAttendee}
          onOpenChange={(v) => {
            if (!v) setCopyAttendee(null);
          }}
          attendee={copyAttendee}
        />
      )}

      {deleteAttendee && (
        <ConfirmDialog
          open={!!deleteAttendee}
          onOpenChange={(v) => {
            if (!v) setDeleteAttendee(null);
          }}
          title="Eliminar inscrito"
          description={`¿Estás seguro de que deseas eliminar a "${deleteAttendee.fullName}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => {
            deleteMutation.mutate(deleteAttendee.id);
            setDeleteAttendee(null);
          }}
        />
      )}
    </main>
  );
}
